import * as FullStory from "@fullstory/browser";
import * as Sentry from "@sentry/react";
import { flash } from "@xola/ui-kit";
import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { isDateString, mapObject } from "common";
import dayjs from "dayjs";
import { capitalize, isArray, isNil, isObject, isPlainObject, memoize } from "lodash";
import { isPlural, singular } from "pluralize";
import { ProductFilterField, PurchaseRuleFilterErrorCode, User } from "types";
import { getCredentialHeaders } from "./credentialHeaders";
import { queryClient } from "./queryClient";
import { config } from "@/config";
import { useAccountStore } from "@/stores/accountStore";
import { OAuthSessionStatus, useAuthStore } from "@/stores/authStore";
import { getAccountLockMessage, isAccountLockedResponse } from "@/utils/accountLock";

export enum RequestMethod {
    GET = "get",
    POST = "post",
    PUT = "put",
    PATCH = "patch",
    DELETE = "delete",
    HEAD = "head",
}

// Seller id is not required in partners since we want to fetch partner seller doc as well
const sellerIdNotRequiredEndpoints = new Set(["/delegators", "/partners"]);

/**
 * Modify the outgoing API calls to add relevant headers like the credential and Seller ID
 */
export const authenticate = (requestConfig: AxiosRequestConfig) => {
    const { seller: sellerId } = useAccountStore.getState();
    const data: { user?: User } | undefined = queryClient.getQueryData(["account"]);

    requestConfig.headers = {
        ...requestConfig.headers,
        ...getCredentialHeaders(),
        // This is injected so the API's error logging & tracking system can see who made the API call
        "X-APP": "X2 Seller",
    };

    if (sellerId) {
        requestConfig.headers["X-SELLER-ID"] = sellerId;
    }

    if (data?.user?.id) {
        requestConfig.headers["X-USER-ID"] = data.user.id;
    }

    if (FullStory.isInitialized()) {
        const fsURL = FullStory.getCurrentSessionURL();
        if (fsURL && fsURL.length > 0) {
            requestConfig.headers["X-FULLSTORY-URL"] = fsURL;
        }
    }

    if (sellerId) {
        requestConfig.params = { ...requestConfig.params };

        // Inject seller ID in query params
        const requireSellerId = !sellerIdNotRequiredEndpoints.has(getEndPoint(requestConfig.url));
        if (requireSellerId && requestConfig.url && !hasSellerId(requestConfig.url) && !requestConfig.params?.seller) {
            requestConfig.params.seller = sellerId;
        }
    }

    return requestConfig;
};

const commonErrors: any = {
    401: "Authentication error. Please specify correct email & password",
    403: "Access Denied",
    404: "Resource not found", // This should be overridden at the component level to specify User/Purchase etc.
    423: "Someone else is modifying this resource. Please try again in a minute.",
    429: "We detected too many requests originating from your user account. Application usage has been temporarily disabled. Please try again in 15 minutes.",
    500: "Error 500: An unexpected error occurred",
    503: "Connection timeout. The server is taking too long to respond. Please try again in a few moments.",
    504: "Connection timeout. The server is taking too long to respond. Please try again in a few moments.",
};

/**
 * Checks if the originator of provided axios error is main xola API or not?
 */
const isXolaApiError = (error: AxiosError): boolean => {
    const baseUrl = error.config?.baseURL;
    if (!baseUrl) return false;

    return baseUrl.startsWith(config.xola.url);
};

const getResourceName = (response: AxiosResponse): [string, string] | [] => {
    const {
        config: { url, baseURL },
    } = response;
    const urlObject = new URL(url ?? "", baseURL ?? `${config.xola.url}/api`);
    const [resource, id, ...rest] = urlObject.pathname.split("/").filter((s) => s.length > 0);
    const isRegularApiPath = rest.length === 0 && !!resource && isPlural(resource);
    return isRegularApiPath ? [resource, id] : [];
};

const getErrorMessage = (response: AxiosResponse) => {
    if (isAccountLockedResponse(response)) {
        return getAccountLockMessage(response);
    }

    const [collection, path] = getResourceName(response);

    const errorMessageFromAPI = findValue(response.data, "message");
    let message: string | null = errorMessageFromAPI ?? null;
    if (collection) {
        const resource = path ? collection : singular(collection);
        const id = path ?? "data";

        const errorMap: Record<string, Record<number, string>> = {
            get: {
                404: `${capitalize(resource)} ${id} not found`,
                500: `Error fetching ${resource} ${id}`,
                403: `Access denied while fetching ${resource} ${id}`,
            },
            anyAnother: {
                404: `Error updating ${resource} ${id}`,
                500: `Error updating ${resource} ${id}`,
                403: `Access denied while updating ${resource} ${id}`,
            },
        };
        const {
            config: { method },
            status,
        } = response;
        message = errorMap[!method || method.toLowerCase() === "get" ? "get" : "anyAnother"][status] ?? message;
    }

    const { data }: { data: any } = response;
    if (response.status === 401) {
        // for handling multi Factor Authentication api response for invalid code
        if (data?.error?.code === 401) {
            message = "Invalid Authentication Code";
        } else {
            message = data?.message === "Multi-Factor Authentication Required" ? "Invalid Authentication Code" : null;
        }
    }

    return message ?? commonErrors[response.status];
};

/**
 * Validation failures that belong to the request as a whole rather than a single field arrive as
 * `{ global: [...] }`.
 */
const getGlobalMessage = (data: unknown): string | null => {
    const globalMessage = findValue(data, "global");

    return Array.isArray(globalMessage) && typeof globalMessage[0] === "string" ? globalMessage[0] : null;
};

const resolveResponseErrorMessage = (response: AxiosResponse): string | null => {
    if ([400, 422].includes(response.status)) {
        // This check made to handle any errors in response that comes from fields or field
        const fieldErrors = findValue(response.data, "fields") ?? findValue(response.data, "field");
        const message = findValue(response.data, "message");
        const reason = findValue(response.data, "reason");

        if (fieldErrors && !reason && !message) {
            // Use the findValue function to search for 'fields' property in response.data.field
            if (Array.isArray(fieldErrors)) {
                // If errorField is an array of strings, join them with a comma
                return fieldErrors.join(", ");
            }

            if (typeof fieldErrors === "string") {
                // If errorField is a string, return it directly
                return fieldErrors;
            }

            if (typeof fieldErrors === "object") {
                return Object.values(fieldErrors).join(", ");
            }
        }

        // For Equipment manager to not show error message
        // when create unavailable overbooking scheduler
        const excludeReasons = ["cannot_add_schedule", "cannot_modify_schedule", "cannot_delete_card"];
        if (excludeReasons.includes(reason)) {
            return "";
        }

        if (reason === "bulk_validation_failed" || reason === "bulk_operations_failed") {
            const responseData = response.data as any;
            const operationErrors = responseData?.field?.ops?.errors;
            const fallbackErrors = findValue(responseData, "errors");
            const errors = operationErrors ?? fallbackErrors;

            if (errors && typeof errors === "object") {
                const errorValues = Array.isArray(errors) ? errors : Object.values(errors);
                const uniqueMessages = [
                    ...new Set(
                        errorValues
                            .map((errorValue: any) => {
                                if (typeof errorValue === "string") {
                                    return errorValue;
                                }

                                if (typeof errorValue?.message === "string") {
                                    return errorValue.message;
                                }

                                return null;
                            })
                            .filter((errorMessage): errorMessage is string => !!errorMessage),
                    ),
                ];

                if (uniqueMessages.length > 0) {
                    return uniqueMessages.join(", ");
                }
            }
        }

        return getGlobalMessage(response.data) ?? message ?? null;
    }

    if (response.status === 409) {
        // To show error when trying to book more than available
        // Used when Switching Experience, During Guest Check In Purchase Modification and Print ticket, On Creating new Purchase checkout
        const openCountError = getOpenCountError(response);
        if (openCountError) {
            return openCountError;
        }

        // To show error for duplicate
        const duplicateEmail = findValue(response.data, "email");
        if (!isNil(duplicateEmail)) {
            return "This email is already associated with another user";
        }

        return getGlobalMessage(response.data) ?? findValue(response.data, "message");
    }

    const { data }: { data: any } = response;
    if (data.global && typeof data.global[0] === "string") {
        return data?.global[0];
    }

    return getErrorMessage(response);
};

export const getResponseErrorMessage = (response: AxiosResponse): string => resolveResponseErrorMessage(response) ?? "";

// List of URLs we don't want to show the error toast message
const hideErrorMessageForUrls = [
    /^\/abandonedPurchases/,
    /^\/conversations/,
    /^\/announcements/,
    /^\/noticeFeedbacks/,
    /^\/installations/,
    /^\/plugins/,
];

interface SuppressErrorShowProps extends AxiosRequestConfig {
    skipResponseInterceptor?: boolean;
}

const isSuppressedByToastConfig = (suppressErrorToast: boolean | number[] | undefined, statusCode: number): boolean => {
    if (typeof suppressErrorToast === "boolean") {
        return suppressErrorToast;
    }

    if (Array.isArray(suppressErrorToast)) {
        return suppressErrorToast.includes(statusCode);
    }

    return false;
};

const suppressErrorShow = (
    config: SuppressErrorShowProps,
    suppressRequirement: boolean,
    statusCode: number,
    message?: string | null,
) => {
    // Suppress error message for 404 on events X2-8971
    if (statusCode === 404 && config?.method?.toLowerCase() === RequestMethod.GET && config?.url?.includes("events")) {
        return true;
    }

    /**
     * Suppress 404 when updating purchases X2-9369.
     * After long afk the purchase might be deleted on the BE side, instead of showing error we will just create new one.
     */
    if (
        statusCode === 404 &&
        config?.method?.toLowerCase() === RequestMethod.PUT &&
        config?.url?.includes("purchases")
    ) {
        return true;
    }

    // Suppress 404 when rendering the abandon purchase in the rollback/refund timeline X2-11996
    if (
        statusCode === 404 &&
        config?.method?.toLowerCase() === RequestMethod.GET &&
        config?.url?.includes("purchases")
    ) {
        return true;
    }

    // Supress 400 for "guest_not_found" error message X2-12159
    if (statusCode === 400 && message === "guest_not_found") {
        return true;
    }

    // Suppress 400 when saving collection item batch update error
    if (
        statusCode === 400 &&
        config?.url?.includes("collectionNodes") &&
        message === "One or more bulk operations failed"
    ) {
        return true;
    }

    if (isSuppressedByToastConfig(config?.suppressErrorToast, statusCode)) {
        return true;
    }

    if (config?.method?.toUpperCase() === "PATCH" && suppressRequirement) {
        if ("params" in config) {
            const isDryRunRequest = config.params?.dryRun;
            // Make an exception for dryRun calls
            return !isDryRunRequest;
        }

        return true;
    }

    if (config?.url === "/purchases") {
        return Object.keys(config.params ?? {}).some((param: string) => param.includes("notifyAt"));
    }

    if (config?.url?.startsWith("/purchases?") && config.url.includes("notifyAt")) {
        return true;
    }

    if (config?.skipResponseInterceptor) {
        return true;
    }

    return hideErrorMessageForUrls.some((pattern) => pattern.test(config?.url ?? ""));
};

export const hasInProductFilterNotMetError = (error: any) => {
    if (error?.response?.data) {
        const errorData = error.response?.data as { errors: any };
        const inFilterError = errorData?.errors?.find(
            (error_: any) =>
                error_?.code === PurchaseRuleFilterErrorCode.IN_FILTER_NOT_MET &&
                error_.field === ProductFilterField.PRODUCT,
        );

        if (inFilterError) {
            return { error: true, isMembershipError: inFilterError.message?.includes("membership") };
        }
    }

    return { error: false };
};

const captureSentryException = (error: AxiosError, statusCode: number) => {
    try {
        console.debug("Preparing to capture error", { error });
        const message = error.response ? getResponseErrorMessage(error.response) : "";
        if (message && message.length > 0) {
            error.message = `HTTP ${statusCode}: ${message} - ${error.message}`;
            console.debug("Updating error.message to", error.message);
        }

        error.message = error.message.replace(/ - Request failed with status code \d+/, "");

        // Add the current fullstory session URL if it exists into Sentry
        if (FullStory.isInitialized()) {
            const fsURL = FullStory.getCurrentSessionURL();
            if (fsURL && fsURL.length > 0) {
                Sentry.setContext("fullstory_url", { fullstoryUrl: fsURL });
            }
        }

        const responsePayload = error.response?.data ?? "";
        Sentry.setContext("api_response", { response: JSON.stringify(responsePayload, null, 2) });
        Sentry.setTag("http_code", statusCode);

        const pathname = window.location.pathname ?? "";
        const browserShort = /\/(\w+)\//.exec(pathname)?.[0];
        if (browserShort) {
            Sentry.setContext("url", { browserShort, pathname, apiEndpoint: error.config?.url });
            Sentry.setTag("api_request_uri", browserShort);
        }

        Sentry.setContext("api_request_payload", { request: error.config?.data ?? "" });
        const sentryId = Sentry.captureException(error, {
            tags: { section: browserShort, http_code: statusCode, message },
        });
        console.debug("Logged the following to sentry (remove this later)", {
            sentryId,
            message,
            responsePayload,
            api_request_uri: error.config?.url,
            api_request_payload: { request: error.config?.data ?? "" },
            exceptionData: { tags: { http_code: statusCode, message } },
        });
    } catch (error_) {
        console.debug("Sentry exception problem", error_);
    }
};

const isExpiredAccessToken = (error: AxiosError) => {
    return (error.response?.data as { error?: string } | undefined)?.error === "invalid_token";
};

export const showSessionExpired = () => {
    useAccountStore.getState().forget(false);
    useAccountStore.getState().toggleSessionExpiredModal(true);
};

export const createResponseErrorHandler = (instance: AxiosInstance) => async (error: AxiosError) => {
    const statusCode = error.response?.status ?? 200;
    const isUnhandledUnauthorized =
        statusCode === 401 && isXolaApiError(error) && !error.config?.ignoreUnauthorizedErrorInterceptor;
    const canRenew = isUnhandledUnauthorized && isExpiredAccessToken(error) && !error.config?.isRetryAfterRenewal;

    if (canRenew && error.config) {
        // Concurrent 401s share one renewal: `renew` hands back the request already in flight.
        if (await useAuthStore.getState().renew()) {
            return instance.request({ ...error.config, isRetryAfterRenewal: true });
        }

        if (useAuthStore.getState().status === OAuthSessionStatus.SIGNED_OUT) {
            showSessionExpired();
        }

        throw error;
    }

    // Not an expired token and not an opted-out step-up form: the session is over.
    if (isUnhandledUnauthorized) {
        showSessionExpired();
    }

    // A lock is expected behaviour under attack, so capturing it would flood Sentry during the incident.
    const isAccountLocked = isAccountLockedResponse(error.response);
    if ([400, 423, 499, 500].includes(statusCode) && !isAccountLocked) {
        captureSentryException(error, statusCode);
    }

    // If there are multiple errors, we don't want to show the error toast message since modal will be shown
    // This is only for patch requests that is being handled in suppressErrorShow
    let hasMultipleErrors = false;
    if (error?.response?.data) {
        const errorData = error.response.data as { errors: unknown };
        hasMultipleErrors = !!errorData.errors;
    }

    // Check if any of the errors have the code 'IN_FILTER_NOT_MET' to prevent displaying product IDs in the flash message
    const inProductFilterNotMetError = hasInProductFilterNotMetError(error);
    const message = error.response ? getResponseErrorMessage(error.response) : null;

    if (!suppressErrorShow(error.config as SuppressErrorShowProps, hasMultipleErrors, statusCode, message)) {
        if (message && (!inProductFilterNotMetError.error || inProductFilterNotMetError.isMembershipError)) {
            flash.danger(message);
        }

        if (inProductFilterNotMetError.error && !inProductFilterNotMetError.isMembershipError) {
            /** For membership restriction error, the error message is already formatted, so no need to show extra error message */
            flash.danger("This product does not meet the coupon restrictions");
        }
    }

    throw error;
};

export const findValue = (data: any, keyToFind: string): any => {
    if (data === undefined || data === null || typeof data !== "object") {
        return null;
    }

    // by comparing undefined to allow zero values
    if (data[keyToFind] !== undefined) {
        return data[keyToFind];
    }

    for (const key in data) {
        if (!Object.prototype.hasOwnProperty.call(data, key)) {
            // To avoid eslint guard-for-in
            continue;
        }

        if (data[key] !== null && typeof data[key] === "object") {
            const value = findValue(data[key], keyToFind);
            if (value !== undefined) {
                return value;
            }
        }
    }

    return null;
};

// Read only the event's open count. An add-on limit error carries its own nested `open`, which is not event capacity.
export const getOpenCountError = (response: AxiosResponse) => {
    const open = response.data?.field?.open;
    if (!isNil(open)) {
        let text = "No slots are available to accommodate this booking. Please try another date or time";
        if (open > 0) {
            text = `Only ${open} slots are available to accommodate this booking. Please reduce the quantity or try another date or time`;
        }

        return text;
    }
};

const hasSellerId = (url: string): boolean => {
    return url.includes("seller=");
};

export const parseDates = (config: AxiosResponse) => {
    const memoTz = memoize((value: string) => dayjs(value).tz());
    if (isObject(config.data) || isArray(config.data)) {
        config.data = mapObject(config.data, (value) => {
            // Seller timezone may not be taken into account until the seller info is loaded for the first time.
            // See `useAccount` for more info.
            return isDateString(value) ? memoTz(value) : value;
        });
    }

    return config;
};

export const stringifyDates = (config: AxiosRequestConfig) => {
    // Only stringify plain objects/arrays. Skips FormData, Blob, URLSearchParams, ArrayBuffer, File, streams, etc.
    if (isPlainObject(config.data) || isArray(config.data)) {
        config.data = mapObject(config.data, (value) => {
            return dayjs.isDayjs(value) ? value.format() : value;
        });
    }

    return config;
};

/**
 * Returns Endpoint name excluding params for eg, if we get /timeslots?params=.....
 * it will return /timeslots
 */
const getEndPoint = (url = "") => {
    return url.split("?")[0];
};
